// src/components/TermsOfUse.jsx

import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import ProgressBar from './components/ProgressBar';
import FormBackButton from './components/FormBackButton';
import LevelUpScreen from './components/LevelUpScreen';
import DecoDivider from './components/DecoDivider';
import TermsOfUseContent from './terms/TermsOfUseContent';
import ImageFraudTacModal from './components/ImageFraudTacModal';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import {
  SIGNUP_TERMS_API_URL,
  SIGNUP_TERMS_BLOCKED_DEFAULT,
  useSignupTermsAccept,
} from './hooks/useSignupTermsAccept';
import {
  prevReachableSignupRoute,
  getSignupVariant,
  isFirstStep,
} from './utils/signupOrderAb';
import { ensureSignupSession } from './utils/ensureSignupSession';
import { signupCta, signupStepTitle } from './utils/signupUxCopy';
import { usesGuidedCopy, signupUxProgress } from './utils/signupUxAb';
import SignupStepHeading from './components/SignupStepHeading';

const TermsOfUse = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const {
    acceptTerms,
    showLevelUp,
    blockedMessage,
    setBlockedMessage,
    acceptError,
    tacInfo,
    setTacInfo,
    loadingTrap,
    goAfterAccept,
    levelUpCopy,
    patientId,
  } = useSignupTermsAccept();
  // Solange 15/08/2026: tocou o texto dos Termos (dead_click) e não viu o
  // botão abaixo da dobra — foi ao mailto do rodapé. Hint + CTA pinado.
  const [ctaHint, setCtaHint] = useState(false);
  const guided = usesGuidedCopy();

  useEffect(() => {
    const url = SIGNUP_TERMS_API_URL.replace(/\/+$/, '');
    fetch(`${url}/health`, { method: 'GET', mode: 'cors' }).catch(() => {});
    logUxEvent('terms_view', { route: '/termos' });
    // Braço `termos_first`: /termos é o PRIMEIRO passo do funil, então o
    // patientId nasce aqui se o paciente entrar por deep link. Idempotente
    // nos outros braços — nome/e-mail/CPF/telefone já criaram a sessão.
    ensureSignupSession().catch(() => {});
  }, []);

  const onTermsBodyClick = (e) => {
    if (e.target && e.target.closest && e.target.closest('a,button')) return;
    if (ctaHint) return;
    setCtaHint(true);
    logUxEvent('terms_text_tap', { route: '/termos' });
  };

  const handleAcceptRaw = async () => {
    const el = scrollRef.current;
    let scrollDepth = null;
    let scrolledToEnd = null;
    if (el) {
      const maxScroll = el.scrollHeight - el.clientHeight;
      scrollDepth = maxScroll > 0 ? Math.min(1, el.scrollTop / maxScroll) : 1;
      scrolledToEnd = maxScroll <= 0 || el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    }
    return acceptTerms({
      scroll_depth_pct: scrollDepth !== null ? Math.round(scrollDepth * 100) : null,
      scrolled_to_end: scrolledToEnd,
      source: 'terms_page',
    });
  };

  // Anti rage-click: o botao "Aceitar Termos" antigo nao tinha disable nem
  // spinner, e a animacao LevelUpScreen so aparece DEPOIS do setState. Em
  // dispositivos lentos, isso fazia o paciente clicar 2-3x achando que travou
  // (caso vanessacamargo2109 14/05: 3x /registration/complete em 45s para o
  // mesmo patientId). O botão fica travado até o /registration/complete
  // responder: o cooldown fixo de 2.5s reabria o botão no meio da espera
  // (p90 de 8s) e 17-25% dos pacientes aceitavam duas vezes.
  const [isSubmitting, handleAccept] = useFormSubmitGuard(handleAcceptRaw, {
    cooldownMs: 2500,
    holdUntilSettled: true,
  });

  if (loadingTrap) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10600,
          background: '#F9FAFB',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.25rem',
          padding: '1.5rem',
          fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif",
          color: '#6B6B6B',
          textAlign: 'center',
        }}
      >
        <style>{`
          @keyframes terms-trap-spin { to { transform: rotate(360deg); } }
        `}</style>
        <div
          aria-hidden="true"
          style={{
            width: '3rem',
            height: '3rem',
            border: '4px solid rgba(211,47,47,0.15)',
            borderTopColor: '#D32F2F',
            borderRadius: '50%',
            animation: 'terms-trap-spin 0.9s linear infinite',
          }}
        />
      </div>
    );
  }

  if (showLevelUp) {
    const copy = levelUpCopy();
    return (
      <LevelUpScreen
        level={1}
        title={copy.title}
        subtitle={copy.subtitle}
        duration={1800}
        onComplete={goAfterAccept}
      />
    );
  }

  return (
    <>
      <style>{`
        /* Poppins loaded globally in index.html */

        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --radius-lg: 1rem;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-xs: 0 2px 4px rgba(0,0,0,0.04);
          --shadow-sm: 0 4px 10px rgba(0,0,0,0.07);
          --shadow-lg: 0 16px 40px rgba(0,0,0,0.08);
        }

        body {
          margin: 0;
          background: var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
          -webkit-font-smoothing: antialiased;
        }

        /* ===== LAYOUT GERAL DA PÁGINA =====
           Solange 15/08/2026: o CTA precisa ficar na tela. min-height sozinho
           deixava o card crescer com o texto inteiro (~42 mil px) e o
           Aceitar Termos ia para o fim, debaixo do rodapé fixo. Altura
           travada: o texto rola dentro do card; o botão fica pinado. */
        body.has-patient-support-footer:has(.terms-page) {
          padding-bottom: 0;
        }
        .terms-page.page-shell {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          height: 100vh;
          height: 100dvh;
          max-height: 100vh;
          max-height: 100dvh;
          min-height: 0;
          overflow: hidden;
          max-width: none;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          background: var(--bg-page);
        }

        .terms-inner {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          max-width: 480px;
          margin: 0 auto;
          padding: 4.25rem 1rem 0.5rem;
          box-sizing: border-box;
          min-height: 0;
          overflow: hidden;
        }

        /* ===== TÍTULO E CONTEXTO ===== */
        .header-block {
          text-align: center;
          max-width: 26rem;
          margin-top: 0.25rem;
          margin-bottom: 0.75rem;
          flex: 0 0 auto;
        }

        .header-eyebrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
          background: #FFF1F1;
          color: var(--primary);
          font-size: .7rem;
          font-weight: 600;
          line-height: 1.2;
          border-radius: var(--radius-full);
          padding: .5rem .75rem;
          box-shadow: var(--shadow-xs);
          margin-bottom: 1rem;
          white-space: nowrap;
        }

        .header-eyebrow-bullet {
          width: .5rem;
          height: .5rem;
          background: var(--primary);
          border-radius: 50%;
          box-shadow: 0 0 6px rgba(211,47,47,0.6);
        }

        .terms-title {
          font-size: 1.25rem;
          line-height: 1.35;
          font-weight: 600;
          color: var(--text-main);
          margin: 0 0 .5rem;
        }

        .terms-sub {
          font-size: .8rem;
          line-height: 1.4;
          font-weight: 400;
          color: var(--text-dim);
          margin: 0 auto;
        }

        /* ===== CAIXA SCROLLÁVEL DOS TERMOS ===== */
        .terms-card {
          background: var(--surface);
          width: 100%;
          max-width: 28rem;
          flex: 1 1 auto;
          min-height: 0;
          max-height: none;
          display: flex;
          flex-direction: column;
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-sm);
          border: 1px solid rgba(0,0,0,0.03);
          box-sizing: border-box;
          position: relative;
          overflow: hidden;
        }
        .terms-card::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 1.5rem;
          pointer-events: none;
          background: linear-gradient(180deg, rgba(255,255,255,0), #fff);
        }

        /* área rolável real do texto */
        .terms-scroll-area {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding: 1rem;
          font-size: .8rem;
          line-height: 1.55;
          color: var(--text-main);
        }

        .terms-scroll-area p {
          margin: 0 0 1rem;
        }

        .terms-scroll-area a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        /* ===== CTA ACEITAR ===== */
        .accept-wrapper {
          position: relative;
          z-index: 46;
          width: 100%;
          max-width: 28rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 0.75rem;
          flex: 0 0 auto;
        }
        .terms-cta-hint {
          margin: 0 0 0.5rem;
          max-width: 20rem;
          text-align: center;
          font-size: 0.8rem;
          line-height: 1.4;
          color: var(--text-dim);
        }
        .terms-cta-hint.is-alert {
          color: #B91C1C;
          font-weight: 600;
        }

        .btn-accept {
          background: var(--primary);
          color: #FFF;
          border: none;
          border-radius: var(--radius-full);
          box-shadow: var(--shadow-lg);
          font-size: 1rem;
          line-height: 1.2;
          font-weight: 600;
          padding: 1rem 1.5rem;
          min-width: 200px;
          cursor: pointer;
          transition: box-shadow .18s ease, transform .18s ease, opacity .18s ease;
          -webkit-tap-highlight-color: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
        }

        .btn-accept:active {
          transform: scale(.98);
          box-shadow: var(--shadow-sm);
        }

        .btn-accept[disabled] {
          opacity: .75;
          cursor: not-allowed;
        }

        .btn-accept-spinner {
          width: 1rem;
          height: 1rem;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #FFFFFF;
          border-radius: 50%;
          animation: btn-accept-spin .7s linear infinite;
        }
        @keyframes btn-accept-spin {
          to { transform: rotate(360deg); }
        }

        /* ===== RESPONSIVO ===== */
        @media (min-width: 480px) {
          .terms-inner {
            padding: 5rem 2rem 0.75rem;
          }

          .terms-title {
            font-size: 1.4rem;
          }
          .terms-sub {
            font-size: .8rem;
          }
        }

        @media (min-width: 768px) {
          .terms-title {
            font-size: 1.5rem;
          }
        }
      `}</style>

      <div className="page-shell terms-page">
        <div className="terms-inner">
        {/* /termos é aberto de dentro do funil: voltar por histórico preserva
            a posição no braço; fallback para o passo anterior do braço (deep
            link em webview sem stack). No braço `termos_first`, termos é o
            primeiro passo — o back leva para a Home. */}
        <FormBackButton
          onClick={() => {
            const variant = getSignupVariant();
            if (isFirstStep('/termos', variant)) {
              navigate('/');
              return;
            }
            if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
              navigate(-1);
              return;
            }
            const prev = prevReachableSignupRoute('termos', variant);
            navigate(prev || '/');
          }}
        />

        <ProgressBar {...signupUxProgress('/termos', getSignupVariant())} />

        <div className="header-block">
          <h1 className="terms-title deco-headline">
            Termos de Uso e Privacidade
          </h1>
          <p className="terms-sub">Para continuar, toque em Aceitar Termos.</p>
          <DecoDivider variant="chevron" />
        </div>

        <div className="terms-card">
          {guided ? <SignupStepHeading title={signupStepTitle('/termos')} /> : null}
          <div
            className="terms-scroll-area"
            ref={scrollRef}
            onClick={onTermsBodyClick}
          >
            <TermsOfUseContent />
          </div>
        </div>

        <div className="accept-wrapper">
          {acceptError ? (
            <p
              role="alert"
              style={{
                margin: '0 0 0.75rem',
                maxWidth: '20rem',
                textAlign: 'center',
                fontSize: '0.8rem',
                lineHeight: 1.45,
                color: '#B91C1C',
              }}
            >
              {acceptError}
            </p>
          ) : ctaHint ? (
            <p className="terms-cta-hint is-alert">
              O texto não é clicável. Toque no botão vermelho para aceitar e continuar.
            </p>
          ) : null}
          <p className="terms-cta-hint">
            O atendimento usa inteligência artificial (IA), com revisão do médico. Se você não
            concorda com o uso de IA, por favor não aceite: busque por favor atendimento presencial
            ou em outra plataforma.
          </p>
          <button
            type="button"
            className="btn-accept"
            onClick={handleAccept}
            disabled={isSubmitting || !!blockedMessage}
            aria-busy={isSubmitting}
          >
            {signupCta('termos', { guided, submitting: isSubmitting })}
          </button>
        </div>
        </div>
      </div>

      <ImageFraudTacModal
        open={!!tacInfo && !blockedMessage}
        apiBaseUrl={SIGNUP_TERMS_API_URL}
        patientId={patientId}
        tacInfo={tacInfo}
        onAccepted={async () => {
          setTacInfo(null);
          await handleAcceptRaw();
        }}
        onBlocked={(msg) => {
          setTacInfo(null);
          setBlockedMessage(
            msg || SIGNUP_TERMS_BLOCKED_DEFAULT
          );
        }}
      />

      {blockedMessage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-blocked-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10500,
            background: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '1rem',
              padding: '1.5rem 1.5rem 1.25rem',
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
              textAlign: 'left',
            }}
          >
            <h2
              id="terms-blocked-title"
              style={{ margin: '0 0 0.6rem', fontSize: '1.05rem', fontWeight: 700, color: '#B91C1C' }}
            >
              Acesso bloqueado
            </h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#334155', lineHeight: 1.55 }}>
              {blockedMessage}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                width: '100%',
                padding: '0.7rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: '#1E293B',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TermsOfUse;
